import { useState } from 'react';
import propTypes from 'prop-types';
import QRCode from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiCopy, FiCheck } from 'react-icons/fi';

import enteSetupKey from '../../../../assets/images/mfa/ente-1-setup-key.png';
import enteAccountDetails from '../../../../assets/images/mfa/ente-2-account-details.png';
import enteCode from '../../../../assets/images/mfa/ente-3-code.png';
import googleAddCode from '../../../../assets/images/mfa/google-1-add-code.png';
import googleReadQr from '../../../../assets/images/mfa/google-2-read-qr.png';
import googleScan from '../../../../assets/images/mfa/google-3-scan.png';
import googleCode from '../../../../assets/images/mfa/google-4-code.png';
import microsoftScanQr from '../../../../assets/images/mfa/microsoft-1-scan-qr.png';
import microsoftScan from '../../../../assets/images/mfa/microsoft-2-scan.png';
import microsoftCode from '../../../../assets/images/mfa/microsoft-3-code.png';
import { Col, Title, Subtitle, ButtonSubmit } from '../styles';
import {
  StepHeader,
  BackButton,
  ProgressGroup,
  ProgressTrack,
  ProgressSegment,
  StepLabel,
  QrFrame,
  Feedback,
  TutorialTrigger,
  CopyField,
  CopyValue,
  CopyButton,
  CopyTextButton,
} from './styles';
import { TutorialModal } from './modal';

const APP_LABELS = {
  google: 'Google Authenticator',
  microsoft: 'Microsoft Authenticator',
  ente: 'Ente Auth',
};

// Passos do tutorial por app (Google tem 4; demais, 3).
const TUTORIAL_STEPS = {
  google: ['step1', 'step2', 'step3', 'step4'],
  microsoft: ['step1', 'step2', 'step3'],
  ente: ['step1', 'step2', 'step3'],
};

// Uma imagem por passo (índice alinhado a TUTORIAL_STEPS); demais apps só texto.
const TUTORIAL_MEDIA = {
  google: [googleAddCode, googleReadQr, googleScan, googleCode],
  microsoft: [microsoftScanQr, microsoftScan, microsoftCode],
  ente: [enteSetupKey, enteAccountDetails, enteCode],
};

const LoginMfaEnroll = ({ values, setCurrentStep, steps }) => {
  const [t] = useTranslation('login');
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const app = values.mfaApp || 'google';
  const appLabel = APP_LABELS[app] || APP_LABELS.google;
  const media = TUTORIAL_MEDIA[app];
  const stepKeys = TUTORIAL_STEPS[app] || TUTORIAL_STEPS.google;

  const toggleTutorial = () => setTutorialOpen(open => !open);

  const copyTextToClipboard = text => {
    if ('clipboard' in navigator) return navigator.clipboard.writeText(text);
    return Promise.resolve(document.execCommand('copy', true, text));
  };

  const handleCopySecret = () => {
    if (copied) return;
    Promise.resolve(copyTextToClipboard(values.mfaSecret)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const copyLabel = copied
    ? t('two-step.enroll.tutorial.copied')
    : t('two-step.enroll.tutorial.copy');

  const copyButton = (
    <CopyButton
      type="button"
      onClick={handleCopySecret}
      title={copyLabel}
      aria-label={copyLabel}
      $copied={copied}
    >
      {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
    </CopyButton>
  );

  const copyTextButton = (
    <CopyTextButton type="button" onClick={handleCopySecret} $copied={copied}>
      {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
      {copyLabel}
    </CopyTextButton>
  );

  return (
    <Col>
      <StepHeader>
        <BackButton
          type="button"
          onClick={() => setCurrentStep(steps.mfaInstall)}
        >
          <FiChevronLeft size={16} />
          {t('two-step.back')}
        </BackButton>
        <ProgressGroup>
          <ProgressTrack>
            <ProgressSegment $done />
            <ProgressSegment $done />
            <ProgressSegment />
          </ProgressTrack>
          <StepLabel>
            {t('two-step.step-progress', { current: 2, total: 3 })}
          </StepLabel>
        </ProgressGroup>
      </StepHeader>

      <Title>{t('two-step.enroll.setup-title')}</Title>
      <Subtitle>{t('two-step.enroll.setup-subtitle')}</Subtitle>
      <QrFrame $spaced>
        {values.mfaOtpUri && <QRCode value={values.mfaOtpUri} size={150} />}
      </QrFrame>
      <Feedback>{t('two-step.enroll.manual-hint')}</Feedback>
      <CopyField>
        <CopyValue>{values.mfaSecret}</CopyValue>
        {copyButton}
      </CopyField>

      <TutorialTrigger type="button" onClick={toggleTutorial}>
        {t('two-step.enroll.tutorial.open', { app: appLabel })}
      </TutorialTrigger>

      <ButtonSubmit
        block
        color="primary"
        onClick={() => setCurrentStep(steps.mfaVerify)}
      >
        {t('two-step.enroll.continue')}
      </ButtonSubmit>

      <TutorialModal
        open={tutorialOpen}
        toggle={toggleTutorial}
        app={app}
        appLabel={appLabel}
        stepKeys={stepKeys}
        media={media}
        copyTextButton={copyTextButton}
      />
    </Col>
  );
};

export default LoginMfaEnroll;

LoginMfaEnroll.propTypes = {
  values: propTypes.object.isRequired,
  setCurrentStep: propTypes.func.isRequired,
  steps: propTypes.object.isRequired,
};
